import type { Loan } from '../../graphql/types';
import type { LoanDataFormState } from '../../components/LoanDataForm';

/**
 * Pure model for the Borrower Analysis Intake mode (board `4b`).
 *
 * Holds the form groups, the standard borrower document checklist, upload
 * validation and the wording of the extract action. Nothing here touches the
 * network, storage (except the small record helpers at the bottom) or React.
 *
 * Requirements: 5.1, 5.3, 5.4, 5.5, 5.6.
 */

// ── Form (Requirement 5.1) ──────────────────────────────────────────────────

export type BAFieldKey =
  | 'borrowerName'
  | 'coBorrower'
  | 'loanNumber'
  | 'loanAmount'
  | 'purpose'
  | 'loanType'
  | 'term'
  | 'noteRate'
  | 'propertyAddress'
  | 'propertyType'
  | 'occupancy'
  | 'appraisedValue'
  | 'appraisalDate';

export interface BAField {
  key: BAFieldKey;
  label: string;
  placeholder?: string;
}

export interface BAFieldGroup {
  name: 'Borrower' | 'Loan' | 'Property';
  fields: BAField[];
}

export const BA_FIELD_GROUPS: ReadonlyArray<BAFieldGroup> = [
  {
    name: 'Borrower',
    fields: [
      { key: 'borrowerName', label: 'Borrower name', placeholder: 'Full legal name' },
      { key: 'coBorrower', label: 'Co-borrower', placeholder: 'Optional' },
    ],
  },
  {
    name: 'Loan',
    fields: [
      { key: 'loanNumber', label: 'Loan number' },
      { key: 'loanAmount', label: 'Loan amount', placeholder: '$' },
      { key: 'purpose', label: 'Purpose' },
      { key: 'loanType', label: 'Loan type' },
      { key: 'term', label: 'Term' },
      { key: 'noteRate', label: 'Note rate', placeholder: '%' },
    ],
  },
  {
    name: 'Property',
    fields: [
      { key: 'propertyAddress', label: 'Address' },
      { key: 'propertyType', label: 'Property type' },
      { key: 'occupancy', label: 'Occupancy' },
      { key: 'appraisedValue', label: 'Appraised value', placeholder: '$' },
      { key: 'appraisalDate', label: 'Appraisal date', placeholder: 'YYYY-MM-DD' },
    ],
  },
];

export const BA_FIELD_KEYS: ReadonlyArray<BAFieldKey> = BA_FIELD_GROUPS.flatMap((g) =>
  g.fields.map((f) => f.key)
);

/**
 * The fields the Loan record can store today (`updateLoanInfo`). The rest are
 * kept on this device until the backend can store them (see the report: Backend
 * gaps). Co-borrower is local too: `updateLoanInfo` has no co-borrower argument.
 */
export const BACKEND_FIELD_KEYS: ReadonlyArray<BAFieldKey> = [
  'borrowerName',
  'loanNumber',
  'loanAmount',
  'propertyAddress',
];

export type BAFormState = Record<BAFieldKey, string>;

export function emptyForm(): BAFormState {
  return Object.fromEntries(BA_FIELD_KEYS.map((k) => [k, ''])) as BAFormState;
}

/** The Loan record wins for the fields it stores; the rest come from this device. */
export function formFromSources(
  loan: Partial<Loan> | null | undefined,
  extras: Partial<BAFormState> | undefined
): BAFormState {
  const form = emptyForm();
  for (const key of BA_FIELD_KEYS) {
    const local = extras?.[key];
    if (typeof local === 'string') form[key] = local;
  }
  if (loan) {
    form.borrowerName = loan.borrowerName ?? form.borrowerName;
    form.loanNumber = loan.loanNumber ?? form.loanNumber;
    form.loanAmount = loan.loanAmount ?? form.loanAmount;
    form.propertyAddress = loan.propertyAddress ?? form.propertyAddress;
    if (!form.coBorrower && loan.coBorrowerNames?.length) {
      form.coBorrower = loan.coBorrowerNames.filter(Boolean).join(', ');
    }
  }
  return form;
}

/** Maps the Intake form onto the state `validateLoanDataForm` (LoanDataForm) checks. */
export function toLoanDataFormState(form: BAFormState): LoanDataFormState {
  return {
    borrowerName: form.borrowerName,
    loanNumber: form.loanNumber,
    propertyAddress: form.propertyAddress,
    loanAmount: form.loanAmount,
    coBorrowerNames: form.coBorrower
      .split(',')
      .map((n) => n.trim())
      .filter(Boolean),
  };
}

export function pickExtras(form: BAFormState): Partial<BAFormState> {
  const extras: Partial<BAFormState> = {};
  for (const key of BA_FIELD_KEYS) {
    if (!BACKEND_FIELD_KEYS.includes(key)) extras[key] = form[key];
  }
  return extras;
}

export function backendFieldsChanged(a: BAFormState, b: BAFormState): boolean {
  return BACKEND_FIELD_KEYS.some((k) => a[k] !== b[k]);
}

export function formsEqual(a: BAFormState, b: BAFormState): boolean {
  return BA_FIELD_KEYS.every((k) => a[k] === b[k]);
}

// ── Save status (Requirement 5.2) ───────────────────────────────────────────

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export const AUTOSAVE_DELAY_MS = 800;

export function saveStatusLabel(status: SaveStatus): string {
  switch (status) {
    case 'saving':
      return 'Saving…';
    case 'saved':
      return 'Saved';
    case 'error':
      return 'Not saved';
    default:
      return '';
  }
}

// ── Uploads (Requirement 5.3) ───────────────────────────────────────────────

export const MAX_FILE_BYTES = 10 * 1024 * 1024;
export const ACCEPTED_EXTENSIONS: ReadonlyArray<string> = ['pdf', 'png', 'jpg', 'jpeg', 'tif', 'tiff'];
export const ACCEPT_ATTRIBUTE = ACCEPTED_EXTENSIONS.map((e) => `.${e}`).join(',');
export const ACCEPTED_FORMATS_TEXT = 'PDF, PNG, JPEG, TIFF';

function megabytes(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Returns a plain-language reason when the file cannot be uploaded, else null. */
export function validateIntakeFile(file: { name: string; size: number }): string | null {
  const dot = file.name.lastIndexOf('.');
  const ext = dot >= 0 ? file.name.slice(dot + 1).toLowerCase() : '';
  if (!ext || !ACCEPTED_EXTENSIONS.includes(ext)) {
    return ext
      ? `.${ext} files are not supported. Use ${ACCEPTED_FORMATS_TEXT}.`
      : `This file has no extension. Use ${ACCEPTED_FORMATS_TEXT}.`;
  }
  if (file.size <= 0) return 'The file is empty.';
  if (file.size > MAX_FILE_BYTES) {
    return `The file is ${megabytes(file.size)}. Files can be up to 10 MB each.`;
  }
  return null;
}

/**
 * First `{base}_{n}{ext}` not in `taken`, so a re-upload never overwrites a file
 * that is already on the loan. Same naming as DocumentProcessingStep.
 */
export function uniqueFileName(filename: string, taken: ReadonlySet<string>): string {
  if (!taken.has(filename)) return filename;
  const dot = filename.lastIndexOf('.');
  const base = dot >= 0 ? filename.slice(0, dot) : filename;
  const ext = dot >= 0 ? filename.slice(dot) : '';
  let n = 1;
  while (taken.has(`${base}_${n}${ext}`)) n++;
  return `${base}_${n}${ext}`;
}

// ── Checklist (Requirement 5.4) ─────────────────────────────────────────────

export interface ChecklistItem {
  id: string;
  label: string;
  /** Normalised phrases (lower case, words separated by single spaces). */
  phrases: ReadonlyArray<string>;
}

export const BA_CHECKLIST: ReadonlyArray<ChecklistItem> = [
  { id: 'note', label: 'Note', phrases: ['note', 'promissory note'] },
  { id: 'deed-of-trust', label: 'Deed of Trust', phrases: ['deed of trust', 'deed trust'] },
  { id: 'appraisal', label: 'Appraisal', phrases: ['appraisal'] },
  { id: 'w2', label: 'W-2', phrases: ['w 2', 'w2'] },
  {
    id: 'bank-statements',
    label: 'Bank statements (2 months)',
    phrases: ['bank statement', 'bank statements', 'bank stmt'],
  },
  { id: 'credit-report', label: 'Credit report', phrases: ['credit report'] },
];

export interface ChecklistRow {
  id: string;
  label: string;
  received: boolean;
}

function normalise(text: string): string {
  return text
    .toLowerCase()
    .replace(/\.[a-z0-9]{2,4}$/, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function hasPhrase(text: string, phrase: string): boolean {
  return ` ${text} `.includes(` ${phrase} `);
}

export function matchesChecklistItem(item: ChecklistItem, text: string): boolean {
  const norm = normalise(text);
  return norm !== '' && item.phrases.some((p) => hasPhrase(norm, p));
}

export interface ChecklistInput {
  /** Names of files on the loan, uploaded this session or already processed. */
  fileNames: ReadonlyArray<string>;
  /** Document types found by extraction, for example "W-2 Form". */
  documentTypes: ReadonlyArray<string>;
  /** Files the user added with a row's "Upload" button: file name → checklist id. */
  assignments?: Readonly<Record<string, string>>;
}

/** Ticks a row when a file was added for it, or a file name or extracted type matches it. */
export function buildChecklist(
  input: ChecklistInput,
  items: ReadonlyArray<ChecklistItem> = BA_CHECKLIST
): ChecklistRow[] {
  const names = new Set(input.fileNames);
  return items.map((item) => {
    const assigned = Object.entries(input.assignments ?? {}).some(
      ([name, id]) => id === item.id && names.has(name)
    );
    const received =
      assigned ||
      input.fileNames.some((n) => matchesChecklistItem(item, n)) ||
      input.documentTypes.some((t) => matchesChecklistItem(item, t));
    return { id: item.id, label: item.label, received };
  });
}

export function countMissing(rows: ReadonlyArray<ChecklistRow>): number {
  return rows.filter((r) => !r.received).length;
}

// ── Extract action (Requirements 5.5, 5.6) ──────────────────────────────────

export interface ExtractActionInput {
  /** Files that have not been sent for extraction yet. */
  pendingCount: number;
  /** Files already sent for extraction (processed or in progress). */
  submittedCount: number;
  missingCount: number;
  /** Extraction was started before, so only new files are sent. */
  alreadyExtracting: boolean;
  /** An upload is still running. */
  uploading?: boolean;
}

export interface ExtractAction {
  label: string;
  enabled: boolean;
  /** `extract` sends the pending files; `review` opens Extract mode. */
  kind: 'extract' | 'review';
  title?: string;
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

export function missingSuffix(missingCount: number): string {
  return missingCount > 0
    ? ` · ${missingCount} ${plural(missingCount, 'document', 'documents')} missing`
    : '';
}

/**
 * The extract action stays enabled when documents are missing and says how many
 * (5.5). Once extraction has started it offers only the new files (5.6), and
 * with nothing new it opens the extracted data instead.
 */
export function getExtractAction(input: ExtractActionInput): ExtractAction {
  const { pendingCount, submittedCount, missingCount, alreadyExtracting, uploading } = input;
  const suffix = missingSuffix(missingCount);

  if (pendingCount > 0) {
    const noun = plural(pendingCount, 'file', 'files');
    const label = alreadyExtracting
      ? `Extract ${pendingCount} new ${noun} now${suffix}`
      : `Extract ${pendingCount} ${noun} now${suffix}`;
    return uploading
      ? { label, enabled: false, kind: 'extract', title: 'Wait for the uploads to finish.' }
      : { label, enabled: true, kind: 'extract' };
  }

  if (submittedCount > 0 || alreadyExtracting) {
    return { label: `Review extracted data${suffix}`, enabled: true, kind: 'review' };
  }

  return {
    label: 'Extract 0 files — add documents first',
    enabled: false,
    kind: 'extract',
    title: 'Add at least one document to extract.',
  };
}

// ── Per-job record kept on this device ──────────────────────────────────────

export interface IntakeBARecord {
  /** Form fields the Loan record cannot store yet. */
  extras: Partial<BAFormState>;
  /** File name → checklist id, for files added with a row's "Upload" button. */
  assignments: Record<string, string>;
  /** Names of files already sent for extraction. */
  submitted: string[];
}

type StorageLike = Pick<Storage, 'getItem' | 'setItem'>;

export function intakeRecordKey(jobId: string): string {
  return `docmind:intake-ba:${jobId}`;
}

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function emptyRecord(): IntakeBARecord {
  return { extras: {}, assignments: {}, submitted: [] };
}

export function loadIntakeRecord(
  jobId: string,
  storage: StorageLike | null = defaultStorage()
): IntakeBARecord {
  if (!storage) return emptyRecord();
  try {
    const raw = storage.getItem(intakeRecordKey(jobId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return emptyRecord();
    const p = parsed as Record<string, unknown>;
    const record = emptyRecord();
    if (p.extras && typeof p.extras === 'object' && !Array.isArray(p.extras)) {
      for (const [k, v] of Object.entries(p.extras as Record<string, unknown>)) {
        if (BA_FIELD_KEYS.includes(k as BAFieldKey) && typeof v === 'string') {
          record.extras[k as BAFieldKey] = v;
        }
      }
    }
    if (p.assignments && typeof p.assignments === 'object' && !Array.isArray(p.assignments)) {
      for (const [k, v] of Object.entries(p.assignments as Record<string, unknown>)) {
        if (typeof v === 'string') record.assignments[k] = v;
      }
    }
    if (Array.isArray(p.submitted)) {
      record.submitted = p.submitted.filter((v): v is string => typeof v === 'string');
    }
    return record;
  } catch {
    return emptyRecord();
  }
}

export function saveIntakeRecord(
  jobId: string,
  record: IntakeBARecord,
  storage: StorageLike | null = defaultStorage()
): void {
  if (!storage) return;
  try {
    storage.setItem(intakeRecordKey(jobId), JSON.stringify(record));
  } catch {
    // Storage full or blocked: the record stays in memory for this session.
  }
}
